import type { BillingDraftListItem } from '@fos/shared/contracts/billing';
import { type AppErrorCode, isAppErrorCode } from '@fos/shared/errors';
import { billingDraftDisplayStatus, billingMonthName, billingPeriodLabel } from '@fos/shared/labels';
import { formatPLN, type Money, NBSP } from '@fos/shared/money';
import { toLocalTime } from '@fos/shared/time';
import { ApiError, NetworkError } from '../../lib/api';
import { trpc } from '../../lib/trpc';
import type { Tone } from '../../ui/Status';

/**
 * Formatowanie widoków Rozliczeń (Figma 09). Kwoty przez @fos/shared (decimal.js, NBSP), czas w sekundach.
 */

/** Kwota z groszami w walucie zestawienia: „250,00 zł”, „120,00 EUR”. */
export function moneyText(amount: Money, currency = 'PLN'): string {
  return formatPLN(amount, { currency });
}

/** Kwota zaokrąglona do złotówki (sumy, kafelki): „5 220 zł”. */
export function moneyShort(amount: Money, currency = 'PLN'): string {
  return formatPLN(amount, { decimals: false, currency });
}

/** Kwota w tabeli: bez groszy, gdy całkowita („720 zł”), inaczej z groszami („266,67 zł”). */
export function moneyAuto(amount: Money, currency = 'PLN'): string {
  const whole = /^-?\d+(\.0+)?$/.test(amount.trim());
  return formatPLN(amount, { decimals: !whole, currency });
}

/** Stawka „180 zł/h”. */
export function rateText(rate: Money, currency = 'PLN'): string {
  return `${moneyAuto(rate, currency)}/h`;
}

/** Suma czasu w tabeli: 104 400 s → „29:00” (godziny mogą przekraczać 24). */
export function clockText(seconds: number): string {
  const minutes = Math.round(Math.max(0, seconds) / 60);
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;
}

/** Podsumowanie (Figma 09: „29 h 00 min”). */
export function hoursMinutesText(seconds: number): string {
  const minutes = Math.round(Math.max(0, seconds) / 60);
  return `${Math.floor(minutes / 60)}${NBSP}h ${String(minutes % 60).padStart(2, '0')}${NBSP}min`;
}

/** „08:00–15:00” (Figma 09: kolumna Godziny). */
export function hoursRangeText(startedAt: Date | string, endedAt: Date | string): string {
  const s = startedAt instanceof Date ? startedAt : new Date(startedAt);
  const e = endedAt instanceof Date ? endedAt : new Date(endedAt);
  return `${toLocalTime(s)}–${toLocalTime(e)}`;
}

/** „27.09” z daty lokalnej „2026-09-27”. */
export function dayMonthText(date: string): string {
  const [, m, d] = date.split('-');
  return `${d}.${m}`;
}

/** „14.10.2026” z daty lokalnej. */
export function fullDateText(date: string): string {
  const [y, m, d] = date.split('-');
  return `${d}.${m}.${y}`;
}

export function periodText(d: Pick<BillingDraftListItem, 'periodFrom' | 'periodTo'>): string {
  return billingPeriodLabel(d.periodFrom, d.periodTo);
}

/** „Szkółka · wrzesień” (kafelek „Czeka na akceptację”). */
export function clientMonthText(clientName: string, periodFrom: string): string {
  return `${clientName} · ${billingMonthName(periodFrom)}`;
}

export function displayStatus(
  d: Pick<BillingDraftListItem, 'status' | 'paidAt'>,
  settlement = false,
): { label: string; tone: Tone } {
  return billingDraftDisplayStatus(d, { settlement });
}

const MONTH_LOCATIVE = [
  'styczniu',
  'lutym',
  'marcu',
  'kwietniu',
  'maju',
  'czerwcu',
  'lipcu',
  'sierpniu',
  'wrześniu',
  'październiku',
  'listopadzie',
  'grudniu',
] as const;

/** „Opłacone w październiku” (miesiąc „YYYY-MM”). */
export function paidInMonthLabel(month: string): string {
  const m = Number(month.slice(5, 7));
  const word = MONTH_LOCATIVE[m - 1] ?? '';
  return `Opłacone ${word.startsWith('w') ? 'we' : 'w'} ${word}`;
}

/** REST eksportu zestawienia (apps/api/src/routes/billing-exports.ts): PDF do podglądu inline, CSV jako plik. */
export function billingExportUrl(id: string, format: 'csv' | 'pdf'): string {
  return `/api/exports/billing/${id}.${format}`;
}

/**
 * Pobranie eksportu zwykłym GET z ciasteczkiem sesji. Błąd serwera (np. brak „Eksportuje dane”) wraca jako
 * `ApiError` z polskim komunikatem (toast zamiast strony z JSON). Zwraca nazwę zapisanego pliku.
 */
export async function downloadBillingExport(url: string, fallbackName: string): Promise<string> {
  let response: Response;
  try {
    response = await fetch(url, { credentials: 'same-origin' });
  } catch {
    throw new NetworkError();
  }
  if (!response.ok) {
    type ErrorBody = { error?: { code?: unknown; message?: unknown } } | null;
    let body: ErrorBody = null;
    try {
      body = (await response.json()) as ErrorBody;
    } catch {
      body = null;
    }
    const code: AppErrorCode = isAppErrorCode(body?.error?.code)
      ? body.error.code
      : response.status === 403
        ? 'FORBIDDEN'
        : response.status === 401
          ? 'UNAUTHENTICATED'
          : 'INTERNAL';
    throw new ApiError({
      code,
      status: response.status,
      message: typeof body?.error?.message === 'string' ? body.error.message : undefined,
    });
  }
  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') ?? '';
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackName;
  if (typeof URL.createObjectURL !== 'function') return name;
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
  return name;
}

/**
 * Tryb Rozliczeń w workspace (`billing.mode`, DECISIONS 2026-10-09 „Rozliczenia z klientem”): bez systemu faktur —
 * rozliczenia z klientem (Szkic → Wysłane do klienta → Opłacone). Do wczytania (albo przy błędzie) — tryb faktur.
 */
export function useBillingMode(enabled = true): { settlement: boolean; ready: boolean } {
  const query = trpc.billing.mode.useQuery(undefined, { staleTime: 60_000, retry: 1, enabled });
  return { settlement: query.data?.settlement ?? false, ready: !query.isPending };
}
